import React, { useState, useEffect } from "react";
import "./OnboardingModal.css";
import { supabase } from "../SupabaseClient.tsx";

interface OnboardingModalProps {
    isOpen: boolean;
    onComplete: (source: string) => void;
    onSkip: () => void;
}

interface ReferralSource {
    source: string;
    sort_order: number;
}

const OnboardingModal: React.FC<OnboardingModalProps> = ({
    isOpen,
    onComplete,
    onSkip,
}) => {
    const [selectedSource, setSelectedSource] = useState<string>("");
    const [otherText, setOtherText] = useState<string>("");
    const [sourceOptions, setSourceOptions] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        const fetchReferralSources = async () => {
            setLoading(true);
            try {
                const { data, error } = await supabase
                    .from("referral_source")
                    .select("source, sort_order")
                    .order("sort_order", { ascending: true });

                if (error) {
                    console.error("Error fetching referral sources:", error);
                } else if (data) {
                    setSourceOptions(data.map((item: ReferralSource) => item.source));
                }
            } catch (error) {
                console.error("Error fetching referral sources:", error);
            } finally {
                setLoading(false);
            }
        };

        if (isOpen) {
            fetchReferralSources();
        }
    }, [isOpen]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const finalSource =
            selectedSource === "Other" && otherText.trim()
                ? `Other: ${otherText.trim()}`
                : selectedSource;
        if (finalSource) {
            onComplete(finalSource);
        }
    };

    const isSubmitDisabled = () => {
        if (!selectedSource) return true;
        if (selectedSource === "Other" && !otherText.trim()) return true;
        return false;
    };

    if (!isOpen) return null;

    return (
        <div className="onboarding-modal-overlay">
            <div className="onboarding-modal">
                <div className="onboarding-modal-content">
                    <h2>Welcome! 🎉</h2>
                    <p className="onboarding-subtitle">
                        We're excited to have you here. Help us improve by letting us know
                        how you found us.
                    </p>

                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label htmlFor="source-select">
                                How did you learn about this app?
                            </label>
                            <select
                                id="source-select"
                                value={selectedSource}
                                onChange={(e) => {
                                    setSelectedSource(e.target.value);
                                    setOtherText("");
                                }}
                                className="source-dropdown"
                                disabled={loading}
                            >
                                <option value="">
                                    {loading ? "Loading options..." : "Select an option..."}
                                </option>
                                {sourceOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {selectedSource === "Other" && (
                            <div className="form-group other-input-group">
                                <label htmlFor="other-input">Please specify:</label>
                                <input
                                    id="other-input"
                                    type="text"
                                    value={otherText}
                                    onChange={(e) => setOtherText(e.target.value)}
                                    placeholder="Tell us how you found us..."
                                    className="other-input"
                                    autoFocus
                                />
                            </div>
                        )}

                        <button
                            type="submit"
                            className="submit-button"
                            disabled={isSubmitDisabled()}
                        >
                            Continue
                        </button>
                        <button type="button" className="skip-button" onClick={onSkip}>
                            Skip for now
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default OnboardingModal;
